/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsNumberFormView.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Number create/edit form - following Extensions module pattern
    with 4-column inline layout.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Phone, Trash2 } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { ROUTES } from '../../../constants/routes';
import { ModuleFormHeader } from '../../../components/layouts';
import { FixedActionBar } from '../../../components/ui/FixedActionBar';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Toggle } from '../../../components/ui/Toggle';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Select, { SelectOption } from '../../../components/ui/select';
import ConfirmationModal from '../../../components/ui/ConfirmationModal';
import { SmsNumberFormData, SmsNumberType, SmsProvider } from '../types';
import { fetchNumber, fetchProviders, createNumber, updateNumber, deleteNumber } from '../api';

const NUMBER_TYPE_OPTIONS: SelectOption[] = [
    { value: 'long_code', label: 'Long Code' },
    { value: 'toll_free', label: 'Toll-Free' },
    { value: 'short_code', label: 'Short Code' },
    { value: 'alphanumeric', label: 'Alphanumeric' },
];

const createDefaultFormData = (): SmsNumberFormData => ({
    providerId: 0,
    phoneNumber: '',
    numberType: 'long_code',
    friendlyName: '',
    enabled: true,
});

export default function SmsNumberFormView() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();

    const isEditMode = id && id !== 'new';

    // Form state
    const [formData, setFormData] = useState<SmsNumberFormData>(createDefaultFormData);
    const [originalFormData, setOriginalFormData] = useState<SmsNumberFormData>(createDefaultFormData);
    const [providers, setProviders] = useState<SmsProvider[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Computed values
    const hasChanges = useMemo(() =>
        JSON.stringify(formData) !== JSON.stringify(originalFormData),
        [formData, originalFormData]
    );

    // Provider options
    const providerOptions = useMemo((): SelectOption[] => {
        return providers.filter(p => p.enabled).map(p => ({
            value: p.id.toString(),
            label: p.name
        }));
    }, [providers]);

    // Load data
    useEffect(() => {
        loadProviders();
        if (isEditMode && id) {
            loadNumber();
        }
    }, [id]);

    const loadProviders = async () => {
        try {
            const data = await fetchProviders();
            setProviders(data);
        } catch (error) {
            console.error('Failed to load providers:', error);
        }
    };

    const loadNumber = async () => {
        try {
            setIsLoading(true);
            const number = await fetchNumber(id!);
            if (number) {
                const data: SmsNumberFormData = {
                    providerId: number.providerId,
                    phoneNumber: number.phoneNumber,
                    numberType: number.numberType,
                    friendlyName: number.friendlyName || '',
                    enabled: number.enabled,
                };
                setFormData(data);
                setOriginalFormData(data);
            }
        } catch (error) {
            showError(error.message || t('sms.numbers.messages.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    // Field handlers
    const handleFieldChange = useCallback((field: keyof SmsNumberFormData, value: unknown) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const { [field]: _, ...rest } = prev;
                return rest;
            });
        }
    }, [errors]);

    // Validation
    const validateForm = useCallback(() => {
        const newErrors: Record<string, string> = {};

        if (!formData.phoneNumber) {
            newErrors.phoneNumber = t('validation.required');
        } else if (!/^\+[1-9]\d{1,14}$/.test(formData.phoneNumber)) {
            newErrors.phoneNumber = t('sms.numbers.invalidE164');
        }

        if (!formData.providerId) {
            newErrors.providerId = t('validation.required');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, t]);

    // Save handler
    const handleSave = useCallback(async () => {
        if (!validateForm()) return undefined;

        try {
            setIsSaving(true);
            let createdId: string | number | undefined;
            if (isEditMode) {
                await updateNumber(id!, formData);
                showSuccess(t('sms.numbers.messages.updateSuccess'));
            } else {
                const res = await createNumber(formData);
                createdId = (res as any)?.uuid ?? (res as any)?.id;
                showSuccess(t('sms.numbers.messages.createSuccess'));
            }
            setOriginalFormData(formData);
            return { success: true, createdId };
        } catch (error) {
            showError(error.message || t('sms.numbers.messages.saveError'));
            return undefined;
        } finally {
            setIsSaving(false);
        }
    }, [formData, isEditMode, id, validateForm, showSuccess, showError, t]);

    // Action bar handlers
    const handleSaveWithAction = useCallback(async (action: 'save' | 'save-and-close' | 'save-and-new') => {
        const result = await handleSave();
        if (result?.success) {
            if (action === 'save' && !isEditMode && result.createdId) {
                navigate(ROUTES.SMS_NUMBER_DETAIL(result.createdId));
            } else if (action === 'save-and-close') {
                navigate(ROUTES.SMS_NUMBERS);
            } else if (action === 'save-and-new') {
                setFormData(createDefaultFormData());
                setOriginalFormData(createDefaultFormData());
                navigate(ROUTES.SMS_NUMBERS_NEW);
            }
        }
    }, [handleSave, navigate, isEditMode]);

    const handleCancel = useCallback(() => {
        if (hasChanges) {
            setFormData(originalFormData);
        } else {
            navigate(ROUTES.SMS_NUMBERS);
        }
    }, [hasChanges, originalFormData, navigate]);

    const handleDelete = useCallback(async () => {
        if (!isEditMode) return;

        try {
            setIsDeleting(true);
            await deleteNumber(id!);
            showSuccess(t('sms.numbers.messages.deleteSuccess'));
            navigate(ROUTES.SMS_NUMBERS);
        } catch (error) {
            showError(error.message || t('sms.numbers.messages.deleteError'));
        } finally {
            setIsDeleting(false);
            setShowDeleteModal(false);
        }
    }, [isEditMode, id, showSuccess, showError, navigate, t]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-muted-foreground">
                    {t('common.loading')}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden p-6 pb-0">
            {/* Header */}
            <ModuleFormHeader
                title={isEditMode ? formData.phoneNumber || t('sms.numbers.editTitle') : t('sms.numbers.createTitle')}
                icon={<Phone className="h-5 w-5 text-primary" />}
                backButtonText={t('common.list', 'List')}
                onBack={() => navigate(ROUTES.SMS_NUMBERS)}
            />

            {/* Scrollable Form Content */}
            <div className="flex-1 overflow-auto pb-24 mt-8">
                <FormBox
                    title={t('sms.numbers.title')}
                    icon={<Phone className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        {/* Row 1: Phone Number | Provider */}
                        <SimpleTooltip content={t('sms.numbers.fields.phoneNumber')} position="top">
                            <Label htmlFor="phoneNumber" required className="text-left">
                                {t('sms.numbers.fields.phoneNumber')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
                            placeholder="+15551234567"
                            error={errors.phoneNumber}
                        />
                        <SimpleTooltip content={t('sms.numbers.fields.provider')} position="top">
                            <Label htmlFor="providerId" required className="text-left">
                                {t('sms.numbers.fields.provider')}
                            </Label>
                        </SimpleTooltip>
                        <Select
                            value={formData.providerId?.toString() || ''}
                            onValueChange={(value) => handleFieldChange('providerId', parseInt(value))}
                            options={providerOptions}
                            placeholder={t('sms.numbers.selectProvider')}
                            searchable={true}
                        />

                        {/* Row 2: Number Type | Friendly Name */}
                        <SimpleTooltip content={t('sms.numbers.fields.type')} position="top">
                            <Label htmlFor="numberType" className="text-left">
                                {t('sms.numbers.fields.type')}
                            </Label>
                        </SimpleTooltip>
                        <Select
                            value={formData.numberType}
                            onValueChange={(value) => handleFieldChange('numberType', value as SmsNumberType)}
                            options={NUMBER_TYPE_OPTIONS}
                            searchable={true}
                        />
                        <SimpleTooltip content={t('sms.numbers.fields.friendlyName')} position="top">
                            <Label htmlFor="friendlyName" className="text-left">
                                {t('sms.numbers.fields.friendlyName')}
                            </Label>
                        </SimpleTooltip>
                        <Input
                            id="friendlyName"
                            value={formData.friendlyName || ''}
                            onChange={(e) => handleFieldChange('friendlyName', e.target.value)}
                            placeholder={t('sms.numbers.fields.friendlyNamePlaceholder')}
                        />

                        {/* Row 3: Enabled */}
                        <SimpleTooltip content={t('sms.numbers.fields.enabled')} position="top">
                            <Label htmlFor="enabled" className="text-left">
                                {t('sms.numbers.fields.enabled')}
                            </Label>
                        </SimpleTooltip>
                        <Toggle
                            id="enabled"
                            value={formData.enabled}
                            onChange={(value) => handleFieldChange('enabled', value)}
                        />
                        <div /><div />
                    </div>
                </FormBox>
            </div>

            {/* Fixed Action Bar */}
            <FixedActionBar
                isEditMode={isEditMode}
                isSaving={isSaving}
                onSaveWithAction={handleSaveWithAction}
                useSaveDropdown={true}
                onCancel={handleCancel}
                onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
                hasChanges={hasChanges}
            />

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title={t('sms.numbers.deleteTitle')}
                message={`${t('sms.numbers.messages.confirmDelete')} (${formData.phoneNumber})`}
                confirmText={t('common.delete')}
                cancelText={t('common.cancel')}
                type="danger"
                isLoading={isDeleting}
            />
        </div>
    );
}
